import { LitElement, html, css } from 'lit'
import { store } from '../store.js'
import { api } from '../api.js'
import { toast } from './app-toast.js'

// Pill wording per integration status. Anything unmapped falls back to the raw
// status, so a new server-side state shows up rather than silently reading
// "not connected".
const STATUS_LABEL = {
  connected: 'Connected',
  disconnected: 'Not connected',
  skipped: 'Skipped',
  error: 'Error'
}

// Integrations, restyled to the BrandExtract design system: one card per source,
// its left edge chartreuse while connected and grey while not, with a status pill
// and the connect/disconnect action on the right.
class PageSettings extends LitElement {
  static properties = {
    _integrations: { state: true },
    _credFor: { state: true },
    _busy: { state: true }
  }

  static styles = css`
    *, *::before, *::after { box-sizing: border-box; }

    .head { margin-bottom: 26px; }
    .eyebrow { font-family: var(--font-eyebrow); font-size: 11px; font-weight: 700;
               letter-spacing: 0.16em; text-transform: uppercase; color: var(--neutral-500);
               margin-bottom: 8px; }
    h1 { margin: 0; font-family: var(--font-display); font-size: 34px; line-height: 1.1;
         font-weight: 700; color: var(--eggplant-500); }
    .count { margin: 10px 0 0; font-size: 14.5px; color: var(--neutral-600); }
    .count b { color: var(--eggplant-500); }

    .card { background: var(--white); border: 1px solid var(--neutral-100); border-radius: 10px;
            box-shadow: var(--shadow-cell); padding: 18px 22px; margin-bottom: 12px;
            border-left: 4px solid var(--neutral-200);
            transition: box-shadow .16s ease, transform .16s ease; }
    .card:hover { box-shadow: var(--shadow-row); transform: translateY(-1px); }
    .card.connected { border-left-color: var(--chartreuse-500); }

    .row { display: flex; align-items: center; justify-content: space-between; gap: 24px; }
    .info { min-width: 0; }
    .name { margin: 0; font-size: 16.5px; font-weight: 700; color: var(--eggplant-500); }
    .desc { margin: 4px 0 0; font-size: 14px; line-height: 1.45; color: var(--neutral-600); }
    .err { margin: 6px 0 0; font-size: 13px; font-weight: 600; color: #B4232B; }

    .controls { display: flex; align-items: center; gap: 16px; flex-shrink: 0; }
    .status { font-family: var(--font-eyebrow); font-size: 10px; font-weight: 700;
              letter-spacing: 0.1em; text-transform: uppercase; white-space: nowrap;
              border-radius: 999px; padding: 5px 12px;
              color: var(--neutral-600); background: var(--neutral-100);
              border: 1px solid var(--neutral-200); }
    .status.connected { color: var(--chartreuse-800); background: var(--chartreuse-100);
                        border-color: var(--chartreuse-300); }
    .status.error { color: #B4232B; background: #FDF4F4; border-color: #F0C9CB; }

    /* Design-system buttons: squared off. Eggplant fill to connect, outline to
       disconnect. */
    button.btn { font-family: var(--font-sans); font-size: 14px; font-weight: 600; line-height: 20px;
                 padding: 8px 16px; cursor: pointer; border: none; border-radius: 0;
                 white-space: nowrap; background: var(--eggplant-500); color: var(--neutral-50);
                 transition: background-color .18s ease, color .18s ease; }
    button.btn:hover:not(:disabled) { background: var(--eggplant-700); }
    button.btn.outline { background: transparent; color: var(--eggplant-500);
                         box-shadow: inset 0 0 0 1px var(--eggplant-500); }
    button.btn.outline:hover:not(:disabled) { background: var(--eggplant-500); color: var(--neutral-50); }
    button.btn:disabled { opacity: 0.4; cursor: not-allowed; }

    .creds { display: flex; gap: 8px; margin-top: 16px; padding-top: 16px;
             border-top: 1px solid var(--neutral-100); flex-wrap: wrap; }
    .creds input { flex: 1; min-width: 180px; height: 38px; font-family: var(--font-sans);
                   font-size: 14px; color: var(--eggplant-500); background: var(--white);
                   border: none; box-shadow: inset 0 0 0 1px var(--border-input);
                   border-radius: 0; padding: 8px 12px; outline: none; }
    .creds input:focus { box-shadow: inset 0 0 0 2px var(--eggplant-500); }
    .creds input::placeholder { color: var(--neutral-500); }
    .hint { color: var(--neutral-600); font-size: 13px; margin-top: 10px; line-height: 1.5; }

    @media (max-width: 640px) {
      h1 { font-size: 26px; }
      .row { flex-direction: column; align-items: flex-start; gap: 14px; }
      .controls { width: 100%; justify-content: space-between; }
    }
  `

  connectedCallback() {
    super.connectedCallback()
    this._integrations = store.get().integrations || []
    this._refresh()
  }

  async _refresh() {
    this._integrations = await api.get('/integrations')
    store.set({ integrations: this._integrations })
  }

  async _connect(p) {
    if (p.connect === 'credentials') {
      this._credFor = this._credFor === p.provider ? null : p.provider
      return
    }
    this._busy = p.provider
    try {
      const { authUrl } = await api.get(`/integrations/${p.provider}/connect`)
      window.location.href = authUrl
    } catch (err) {
      toast(err.message, 'error')
      this._busy = null
    }
  }

  async _submitCreds(e, p) {
    e.preventDefault()
    const fd = new FormData(e.target)
    this._busy = p.provider
    try {
      await api.post(`/integrations/${p.provider}/connect`, {
        accessToken: fd.get('accessToken'),
        externalAccountId: fd.get('externalAccountId') || undefined
      })
      this._credFor = null
      await this._refresh()
      toast(`${p.label} connected`, 'success')
    } catch (err) {
      toast(err.message, 'error')
    } finally {
      this._busy = null
    }
  }

  async _disconnect(p) {
    this._busy = p.provider
    try {
      await api.delete(`/integrations/${p.provider}`)
      await this._refresh()
    } catch (err) {
      toast(err.message, 'error')
    } finally {
      this._busy = null
    }
  }

  render() {
    const items = this._integrations || []
    const connected = items.filter(p => p.status === 'connected').length
    return html`
      <div class="head">
        <div class="eyebrow">Settings</div>
        <h1>Integrations</h1>
        <p class="count"><b>${connected} of ${items.length}</b> sources connected.</p>
      </div>

      ${items.map(p => html`
        <div class="card ${p.status === 'connected' ? 'connected' : ''}">
          <div class="row">
            <div class="info">
              <p class="name">${p.label}</p>
              <p class="desc">${p.description}</p>
              ${p.lastError ? html`<p class="err">${p.lastError}</p>` : ''}
            </div>
            <div class="controls">
              <span class="status ${p.status}">${STATUS_LABEL[p.status] || p.status}</span>
              ${p.status === 'connected'
                ? html`<button class="btn outline" ?disabled=${this._busy === p.provider}
                          @click=${() => this._disconnect(p)}>Disconnect</button>`
                : html`<button class="btn" ?disabled=${this._busy === p.provider}
                          @click=${() => this._connect(p)}>
                          ${p.status === 'error' ? 'Reconnect' : 'Connect'}</button>`}
            </div>
          </div>
          ${this._credFor === p.provider ? html`
            <form class="creds" @submit=${(e) => this._submitCreds(e, p)}>
              <input name="accessToken" placeholder="${p.label} API token" required />
              <input name="externalAccountId" placeholder="Account / user id (optional)" />
              <button class="btn" type="submit" ?disabled=${this._busy === p.provider}>Save</button>
            </form>
            ${p.provider === 'workamajig' ? html`
              <div class="hint">
                To find your Workamajig API token, click your name in the upper-right
                corner of the Workamajig interface and find the “API User Token.”
                Click it, then click copy to clipboard.
              </div>` : ''}` : ''}
        </div>
      `)}
    `
  }
}

customElements.define('page-settings', PageSettings)
